import { gql } from '@chronosphereio/core';
import { Client } from 'urql';
import { urqlClient } from '@/clients/urql-client';
import { AnalyticsEventInput } from '@/generated/graphql';

const LOG_EVENTS_MUTATION = gql`
  mutation LogEvents($input: LogEventsInput!) {
    logEvents(input: $input)
  }
`;

/**
 * ChronosphereUiAnalytics is a client that sends analytic events that we wish to surface
 * _in_ the product and not for debugging/analysis purposes.
 */
type BufferedEvent = Omit<AnalyticsEventInput, 'sessionId' | 'userId'>;

export class ChronosphereUiAnalytics {
  private urqlClient: Client;
  private userId: string | undefined;
  private sessionId: string | undefined;
  private eventBuffer: BufferedEvent[] = [];
  private generateUUID: () => string;
  constructor(urqlClient: Client, generateUUID: () => string) {
    this.urqlClient = urqlClient;
    this.generateUUID = generateUUID;
  }

  setUserId(userId: string): void {
    this.userId = userId;
    this.flushBufferedEvents();
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    this.flushBufferedEvents();
  }

  private flushBufferedEvents(): void {
    if (this.eventBuffer.length === 0 || this.userId === undefined || this.sessionId === undefined) {
      return;
    }
    const events: AnalyticsEventInput[] = this.eventBuffer.map((event) => ({
      ...event,
      userId: this.userId ?? '',
      sessionId: this.sessionId ?? '',
    }));
    this.eventBuffer = [];
    this.sendEvents(events);
  }

  trackPageView(previousHref?: string): void {
    const eventData: BufferedEvent = {
      eventType: 'navigation',
      eventName: 'page_view',
      eventId: this.generateUUID(),
      eventTime: new Date().toISOString(),
      pageData: {
        pageName: window.location.pathname,
        pageUrl: window.location.href,
        pageTitle: document.title,
        pageReferrer: previousHref,
        userAgent: navigator.userAgent,
      },
    };

    if (this.userId === undefined || this.sessionId === undefined) {
      this.eventBuffer.push(eventData);
      return;
    }

    const event: AnalyticsEventInput = {
      ...eventData,
      sessionId: this.sessionId,
      userId: this.userId,
    };
    this.sendEvents([event]);
  }

  private sendEvents(events: AnalyticsEventInput[]): void {
    try {
      // No need to do anything with the result.
      this.urqlClient
        .mutation(LOG_EVENTS_MUTATION, { input: { events } })
        .toPromise()
        .catch((error) => {
          console.error('Error logging events', error);
        });
    } catch (error) {
      console.error('Error logging events', error);
    }
  }
}

let chronosphereUiAnalytics: ChronosphereUiAnalytics | undefined = undefined;

const getUUIDGenerator = (): (() => string) => {
  if (process.env.NODE_ENV === 'test') {
    return () => `test-uuid-${Math.random().toString(36).slice(2)}`;
  }
  return () => crypto.randomUUID();
};

export const getChronosphereUiAnalytics = (): ChronosphereUiAnalytics => {
  if (chronosphereUiAnalytics === undefined) {
    chronosphereUiAnalytics = new ChronosphereUiAnalytics(urqlClient, getUUIDGenerator());
  }
  return chronosphereUiAnalytics;
};
